import { useEffect, useMemo, useRef } from "react";
import { Loader2, Users } from "lucide-react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useDashboardChart } from "@/hooks/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { TableRow, TableCell } from "@/components/ui/table";
import {
  DashboardTable,
  useDashboardTable,
  type DashboardTableColumn,
} from "./dashboard-table";
import { useDashboardUrlState } from "./useDashboardUrlState";
import { FiltersCard } from "./filters-card";

const DASHBOARD_ID = "discount-codes";

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().split("T")[0]!;
}

const QUICK_PATTERNS = ["recruit", "bhg", "black", ""] as const;

function formatNok(amountInOere: string | number | null | undefined): string {
  if (amountInOere == null || amountInOere === "") return "—";
  const num = typeof amountInOere === "number" ? amountInOere : Number(amountInOere);
  if (!Number.isFinite(num)) return String(amountInOere);
  return (num / 100).toLocaleString("no-NO", {
    maximumFractionDigits: 2,
  });
}

const USES_COLOR = "#6B8AFF";
const REVENUE_COLOR = "#D0A84C";
const axisStyle = { fontSize: 11, fill: "rgba(244, 244, 245, 0.60)" };

type UsageRow = { day: string; uses: number | string; revenue: string };

type CodeTotalRow = {
  code: string;
  uses: number | string;
  unique_users: number | string;
  gross_revenue: string;
};

type UserForCodeRow = {
  given_name: string | null;
  family_name: string | null;
  email: string | null;
  charge_date: string;
  amount: string;
  tier: string | null;
  raw_code: string;
  auth_user_id: string;
};

const CODE_TOTALS_COLUMNS: DashboardTableColumn<CodeTotalRow>[] = [
  { id: "code", label: "Kode", value: (r) => r.code },
  { id: "uses", label: "Bruk", value: (r) => r.uses, numeric: true, align: "right" },
  {
    id: "unique_users",
    label: "Unike brukere",
    value: (r) => r.unique_users,
    numeric: true,
    align: "right",
  },
  {
    id: "gross_revenue",
    label: "Brutto omsetning (NOK)",
    value: (r) => r.gross_revenue,
    numeric: true,
    align: "right",
  },
  { id: "actions", label: "", sortable: false },
];

const USERS_FOR_CODE_COLUMNS: DashboardTableColumn<UserForCodeRow>[] = [
  {
    id: "name",
    label: "Navn",
    value: (r) => [r.given_name, r.family_name].filter(Boolean).join(" "),
  },
  { id: "email", label: "E-post", value: (r) => r.email },
  { id: "charge_date", label: "Dato", value: (r) => r.charge_date },
  { id: "raw_code", label: "Full kode", value: (r) => r.raw_code },
  {
    id: "amount",
    label: "Beløp (NOK)",
    value: (r) => r.amount,
    numeric: true,
    align: "right",
  },
];

function UsageOverTimeChart({ rows }: { rows: UsageRow[] }) {
  const data = useMemo(
    () =>
      rows.map((r) => ({
        day: r.day,
        uses: Number(r.uses),
        revenueNok: Number(r.revenue) / 100,
      })),
    [rows]
  );

  return (
    <div className="rounded-md border border-admin-card-border bg-admin-card p-4">
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#2B2B31" />
          <XAxis dataKey="day" tick={axisStyle} />
          <YAxis
            yAxisId="uses"
            orientation="left"
            tick={axisStyle}
            allowDecimals={false}
            stroke={USES_COLOR}
            width={36}
          />
          <YAxis
            yAxisId="revenue"
            orientation="right"
            tick={axisStyle}
            tickFormatter={(v: number) => v.toLocaleString("no-NO")}
            stroke={REVENUE_COLOR}
            width={64}
          />
          <Tooltip content={<DualTooltip />} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Line
            yAxisId="uses"
            type="monotone"
            dataKey="uses"
            name="Bruk (antall)"
            stroke={USES_COLOR}
            strokeWidth={2}
            dot={{ fill: USES_COLOR, r: 3 }}
            activeDot={{ r: 5 }}
          />
          <Line
            yAxisId="revenue"
            type="monotone"
            dataKey="revenueNok"
            name="Brutto omsetning (NOK)"
            stroke={REVENUE_COLOR}
            strokeWidth={2}
            dot={{ fill: REVENUE_COLOR, r: 3 }}
            activeDot={{ r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function DualTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-admin-card-border bg-admin-card px-3 py-2 shadow-lg">
      <p className="text-xs font-medium text-admin-text-primary mb-1">{label}</p>
      {payload.map((entry: any, i: number) => (
        <p key={i} className="text-xs" style={{ color: entry.color }}>
          {entry.name}:{" "}
          {typeof entry.value === "number"
            ? entry.value.toLocaleString("no-NO", { maximumFractionDigits: 2 })
            : entry.value}
        </p>
      ))}
    </div>
  );
}

export function DiscountCodesDashboard() {
  // Every visible filter / selection is in the URL — share the URL, share the view.
  const [{ from, to, pattern, selectedCode }, update] = useDashboardUrlState({
    from: isoDaysAgo(120),
    to: isoDaysAgo(0),
    pattern: "",
    selectedCode: "",
  });

  const usersCardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (selectedCode && usersCardRef.current) {
      usersCardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selectedCode]);

  const filterParams = useMemo(
    () => ({ from, to, codePattern: pattern || undefined }),
    [from, to, pattern]
  );

  const usageOverTime = useDashboardChart<typeof filterParams, {
    day: string;
    uses: number | string;
    revenue: string;
  }>(DASHBOARD_ID, "usage-over-time", filterParams);

  const codeTotals = useDashboardChart<typeof filterParams, CodeTotalRow>(
    DASHBOARD_ID, "code-totals", filterParams
  );

  const usersForCodeParams = useMemo(
    () => ({ from, to, code: selectedCode }),
    [from, to, selectedCode]
  );

  const usersForCode = useDashboardChart<typeof usersForCodeParams, UserForCodeRow>(
    DASHBOARD_ID, "users-for-code", usersForCodeParams, {
      enabled: !!selectedCode,
    }
  );

  const codeTotalsRows = useMemo(
    () =>
      (codeTotals.data ?? []).filter(
        (row): row is CodeTotalRow => !!row.code
      ),
    [codeTotals.data]
  );
  const codeTotalsTable = useDashboardTable(
    "code-totals",
    CODE_TOTALS_COLUMNS,
    codeTotalsRows
  );
  const usersForCodeTable = useDashboardTable(
    "users-for-code",
    USERS_FOR_CODE_COLUMNS,
    usersForCode.data
  );

  return (
    <div className="space-y-6">
      <FiltersCard>
        <div className="flex flex-wrap items-end gap-3">
          <label className="space-y-1">
            <span className="text-xs text-admin-text-subtle">Fra</span>
            <Input
              type="date"
              value={from}
              onChange={(e) => update({ from: e.target.value })}
              className="w-44"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs text-admin-text-subtle">Til</span>
            <Input
              type="date"
              value={to}
              onChange={(e) => update({ to: e.target.value })}
              className="w-44"
            />
          </label>
          <label className="space-y-1 flex-1 min-w-[12rem]">
            <span className="text-xs text-admin-text-subtle">Kodefilter (ILIKE)</span>
            <Input
              value={pattern}
              onChange={(e) => update({ pattern: e.target.value })}
              placeholder="f.eks. recruit"
            />
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          {QUICK_PATTERNS.map((p) => (
            <Button
              key={p || "all"}
              variant={pattern === p ? "default" : "outline"}
              size="sm"
              onClick={() => update({ pattern: p })}
            >
              {p === "" ? "Alle" : p}
            </Button>
          ))}
        </div>
      </FiltersCard>

      <Card>
        <CardHeader>
          <CardTitle>Bruk over tid</CardTitle>
        </CardHeader>
        <CardContent>
          {usageOverTime.isLoading ? (
            <ChartSkeleton />
          ) : usageOverTime.isError ? (
            <ErrorBlock message={usageOverTime.error?.message} />
          ) : !usageOverTime.data || usageOverTime.data.length === 0 ? (
            <EmptyBlock>Ingen rabatterte fakturaer i perioden.</EmptyBlock>
          ) : (
            <UsageOverTimeChart rows={usageOverTime.data} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Totaler per kode</CardTitle>
        </CardHeader>
        <CardContent>
          {codeTotals.isLoading ? (
            <ChartSkeleton />
          ) : codeTotals.isError ? (
            <ErrorBlock message={codeTotals.error?.message} />
          ) : !codeTotals.data || codeTotals.data.length === 0 ? (
            <EmptyBlock>Ingen koder matcher filteret.</EmptyBlock>
          ) : (
            <DashboardTable
              table={codeTotalsTable}
              columns={CODE_TOTALS_COLUMNS}
              renderRow={(row) => (
                <TableRow key={row.code}>
                  <TableCell className="font-mono text-admin-text-primary">
                    {row.code}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {String(row.uses)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {String(row.unique_users)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatNok(row.gross_revenue)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant={selectedCode === row.code ? "default" : "outline"}
                      size="sm"
                      onClick={() =>
                        update({
                          selectedCode:
                            selectedCode === row.code ? "" : row.code,
                        })
                      }
                    >
                      <Users className="h-3.5 w-3.5" />
                      Brukere
                    </Button>
                  </TableCell>
                </TableRow>
              )}
            />
          )}
        </CardContent>
      </Card>

      {selectedCode && (
        <Card ref={usersCardRef} className="scroll-mt-4">
          <CardHeader>
            <CardTitle>
              Brukere for{" "}
              <span className="font-mono text-admin-primary">{selectedCode}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {usersForCode.isLoading ? (
              <ChartSkeleton />
            ) : usersForCode.isError ? (
              <ErrorBlock message={usersForCode.error?.message} />
            ) : !usersForCode.data || usersForCode.data.length === 0 ? (
              <EmptyBlock>Ingen brukere fant denne koden i perioden.</EmptyBlock>
            ) : (
              <DashboardTable
                table={usersForCodeTable}
                columns={USERS_FOR_CODE_COLUMNS}
                renderRow={(row, i) => (
                  <TableRow
                    key={`${row.auth_user_id}-${row.charge_date}-${i}`}
                  >
                    <TableCell>
                      {[row.given_name, row.family_name].filter(Boolean).join(" ") || "—"}
                    </TableCell>
                    <TableCell className="text-admin-text-secondary">
                      {row.email ?? "—"}
                    </TableCell>
                    <TableCell className="tabular-nums">{row.charge_date}</TableCell>
                    <TableCell className="font-mono text-xs text-admin-text-subtle break-all">
                      {row.raw_code}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatNok(row.amount)}
                    </TableCell>
                  </TableRow>
                )}
              />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ChartSkeleton() {
  return (
    <div className="flex items-center gap-2 text-admin-text-subtle text-sm py-8 justify-center">
      <Loader2 className="h-4 w-4 animate-spin" />
      Laster…
    </div>
  );
}

function EmptyBlock({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-sm text-admin-text-subtle py-6 text-center">{children}</p>
  );
}

function ErrorBlock({ message }: { message?: string }) {
  return (
    <div className="rounded-md border border-admin-danger-500/30 bg-admin-danger-500/5 p-4 text-sm text-admin-danger-400">
      {message || "Ukjent feil"}
    </div>
  );
}
